import React, { useEffect, useMemo, useState } from "react";

type CreditConfig = {
  id: number;
  service_type: string;
  unit_type: string;
  units_per_credit: number;
  platform_cost_per_unit: number;
  credit_value_usd: number; // read-only
  is_active: boolean;
  updated_at?: string;
  model_used?: string;
};

type ListResponse = {
  configurations: CreditConfig[];
  total_configurations: number;
};

const CREDIT_VALUE_USD = 0.25;
const SLIDER_MIN = 0;
const SLIDER_MAX = 600;

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

const pct = (n: number) => (isFinite(n) ? n : 0);

// Tooltip component
const Tooltip: React.FC<{ content: React.ReactNode; children: React.ReactNode }> = ({ content, children }) => {
  const [show, setShow] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {children}
      </div>
      {show && (
        <div className="absolute z-50 w-80 p-4 bg-gray-900 border border-gray-600 rounded-lg shadow-xl text-xs text-gray-200 -top-2 left-full ml-2">
          {content}
        </div>
      )}
    </div>
  );
};

// Generate calculation summary for tooltip
const getCalculationSummary = (
  platformCost: number,
  unitsPerCredit: number,
  marginPct: number,
  unitType: string
) => {
  const creditPrice = CREDIT_VALUE_USD;
  const marginMultiplier = 1 + (marginPct / 100);

  // Step 1: Convert platform cost to credits
  const platformCostInCredits = platformCost * (1 / creditPrice);

  // Step 2: Apply margin
  const userCostInCredits = platformCostInCredits * marginMultiplier;

  // Step 3: Compute units per credit
  const calculatedUnitsPerCredit = 1 / userCostInCredits;

  return (
    <div className="space-y-2">
      <div className="font-semibold text-blue-300 border-b border-gray-700 pb-1">
        📊 Calculation Summary
      </div>

      <div>
        <div className="text-gray-400 text-[10px] uppercase tracking-wide">Step 1: Platform Cost → Credits</div>
        <div className="font-mono text-[11px] mt-1">
          {platformCost.toFixed(6)} × (1 / {creditPrice}) = <span className="text-green-300">{platformCostInCredits.toFixed(8)}</span> credits/{unitType}
        </div>
      </div>

      <div>
        <div className="text-gray-400 text-[10px] uppercase tracking-wide">Step 2: Apply {marginPct}% Margin</div>
        <div className="font-mono text-[11px] mt-1">
          {platformCostInCredits.toFixed(8)} × {marginMultiplier.toFixed(2)} = <span className="text-green-300">{userCostInCredits.toFixed(8)}</span> credits/{unitType}
        </div>
      </div>

      <div>
        <div className="text-gray-400 text-[10px] uppercase tracking-wide">Step 3: Units per Credit</div>
        <div className="font-mono text-[11px] mt-1">
          1 / {userCostInCredits.toFixed(8)} = <span className="text-green-300 font-semibold">{calculatedUnitsPerCredit.toFixed(2)}</span> {unitType}/credit
        </div>
      </div>

      <div className="pt-2 border-t border-gray-700 text-[10px] text-gray-400">
        <div>💰 Credit Price: ${creditPrice}</div>
        <div>📈 Margin: {marginPct}%</div>
        <div>🎯 Result: {unitsPerCredit.toFixed(2)} {unitType}/credit</div>
      </div>
    </div>
  );
};

function marginPctFrom(upc: number, costPerUnit: number) {
  // margin% = ((userPricePerUnit - platformCostPerUnit) / platformCostPerUnit) * 100
  // where userPricePerUnit = CREDIT_VALUE_USD / upc
  if (!costPerUnit || costPerUnit <= 0 || !upc || upc <= 0) return 0;
  const userPrice = CREDIT_VALUE_USD / upc;

  //log all these values



  return ((userPrice - costPerUnit) / costPerUnit) * 100;
}

function upcFrom(costPerUnit: number, marginPct: number) {
  // upc = CREDIT_VALUE_USD / (costPerUnit * (1 + margin))
  const margin = marginPct / 100;
  const targetPrice = costPerUnit * (1 + margin);
  if (targetPrice <= 0) return 0;
  return CREDIT_VALUE_USD / targetPrice;
}

const CreditConfigPage: React.FC = () => {
  const [rows, setRows] = useState<CreditConfig[]>([]);
  const [originalById, setOriginalById] = useState<Record<number, CreditConfig>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Margin controls
  const [sameMarginForAll, setSameMarginForAll] = useState(true);
  const [masterMarginPct, setMasterMarginPct] = useState(300); // 0..600 valid
  const [rowMargins, setRowMargins] = useState<Record<number, number | undefined>>({});

  const dirtyCount = useMemo(
    () =>
      rows.reduce((n, r) => {
        const o = originalById[r.id];
        if (!o) return n;
        if (
          Number(o.units_per_credit) !== Number(r.units_per_credit) ||
          Number(o.platform_cost_per_unit) !== Number(r.platform_cost_per_unit)
        ) {
          return n + 1;
        }
        return n;
      }, 0),
    [rows, originalById]
  );

  const fetchConfigs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/credit-config");
      if (!res.ok) throw new Error(`GET /credit-config failed: ${res.status}`);
      const data: ListResponse = await res.json();
      const active = (data.configurations || []).filter((c) => c.is_active !== false);
      setRows(active);
      const map: Record<number, CreditConfig> = {};
      active.forEach((c) => (map[c.id] = { ...c }));
      setOriginalById(map);
    } catch (e: any) {
      setError(e?.message || "Failed to load configurations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  // Apply master margin across all rows when toggled/changed
  useEffect(() => {
    if (!sameMarginForAll) return;
    const m = clamp(masterMarginPct, SLIDER_MIN, SLIDER_MAX);
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        units_per_credit: Number(upcFrom(r.platform_cost_per_unit, m).toFixed(6)),
      }))
    );
  }, [sameMarginForAll, masterMarginPct]);

  const applyRowMargin = (id: number, pctVal: number) => {
    const p = clamp(pctVal, SLIDER_MIN, SLIDER_MAX);
    setRowMargins((prev) => ({ ...prev, [id]: p }));
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? { ...r, units_per_credit: Number(upcFrom(r.platform_cost_per_unit, p).toFixed(6)) }
          : r
      )
    );
  };

  const updateRowField = (
    id: number,
    field: "units_per_credit" | "platform_cost_per_unit",
    value: number
  ) => {
    const v = Number(value);
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: v } : r)));

    // keep margins coherent whenever platform cost changes
    if (field === "platform_cost_per_unit") {
      if (sameMarginForAll) {
        const m = clamp(masterMarginPct, SLIDER_MIN, SLIDER_MAX);
        setRows((prev) =>
          prev.map((r) => ({
            ...r,
            units_per_credit: Number(upcFrom(r.platform_cost_per_unit, m).toFixed(6)),
          }))
        );
      } else if (rowMargins[id] !== undefined) {
        applyRowMargin(id, rowMargins[id] as number);
      }
    }
  };

  const resetRowToCurrent = (id: number) => {
    const o = originalById[id];
    if (!o) return;
    setRows((prev) => prev.map((r) => (r.id === id ? { ...o } : r)));
    setRowMargins((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  const resetAll = () => {
    setRows((prev) => prev.map((r) => (originalById[r.id] ? { ...originalById[r.id] } : r)));
    setRowMargins({});
  };

  const saveAll = async () => {
    setSaving(true);
    setError(null);
    try {
      const updates = rows.filter((r) => {
        const o = originalById[r.id];
        return (
          o &&
          (Number(o.units_per_credit) !== Number(r.units_per_credit) ||
            Number(o.platform_cost_per_unit) !== Number(r.platform_cost_per_unit))
        );
      });

      for (const r of updates) {
        const body = {
          units_per_credit: r.units_per_credit,
          platform_cost_per_unit: r.platform_cost_per_unit,
        };
        const res = await fetch(`/api/credit-config/${r.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`PUT /credit-config/${r.id} failed: ${res.status}`);
      }

      await fetchConfigs(); // refresh originals after save
    } catch (e: any) {
      setError(e?.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Credit Configuration</h1>
        <div className="flex items-center gap-3 mt-3 sm:mt-0">
          <button
            onClick={resetAll}
            disabled={saving || dirtyCount === 0}
            className="px-4 py-2 rounded bg-gray-700 text-white border border-gray-600 hover:bg-gray-600 disabled:opacity-50"
          >
            Reset all to current
          </button>
          <button
            onClick={saveAll}
            disabled={saving || dirtyCount === 0}
            className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {saving ? "Saving..." : `Save (${dirtyCount})`}
          </button>
        </div>
      </div>

      {/* Master margin controls */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-white">Margin Controls</h2>
            <p className="text-gray-400 text-sm">
              Base calculation uses <span className="text-blue-300">$0.25</span> per credit.
            </p>
          </div>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              className="h-4 w-4 bg-gray-800 border-gray-600"
              checked={sameMarginForAll}
              onChange={(e) => setSameMarginForAll(e.target.checked)}
            />
            <span className="text-sm text-gray-300">Same margin for all</span>
          </label>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 md:gap-4 items-center">
          <input
            type="range"
            min={SLIDER_MIN}
            max={SLIDER_MAX}
            step={1}
            value={masterMarginPct}
            onChange={(e) => setMasterMarginPct(clamp(Number(e.target.value), SLIDER_MIN, SLIDER_MAX))}
            disabled={!sameMarginForAll}
            className="w-full accent-blue-500"
          />
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={SLIDER_MIN}
              max={SLIDER_MAX}
              step={1}
              value={masterMarginPct}
              onChange={(e) => setMasterMarginPct(clamp(Number(e.target.value), SLIDER_MIN, SLIDER_MAX))}
              disabled={!sameMarginForAll}
              className="w-24 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-white text-right"
            />
            <span className="text-white font-semibold">%</span>
          </div>
        </div>

        {/* <div className="text-xs text-gray-400 mt-2">
          For margin <code>{masterMarginPct}%</code>: per‑unit user price = <code>platform_cost_per_unit × (1 + {masterMarginPct / 100})</code>,
          so <code>units_per_credit = 0.2 ÷ price</code>.
        </div> */}
      </div>

      {/* Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-3 sm:p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left text-gray-400 pb-2">Service</th>
              <th className="text-left text-gray-400 pb-2">Unit</th>
              <th className="text-right text-gray-400 pb-2">Platform $/unit</th>
              <th className="text-right text-gray-400 pb-2">Units per credit</th>
              <th className="text-right text-gray-400 pb-2">Cost per credit</th>
              <th className="text-right text-gray-400 pb-2">Margin % (Saved / Edited)</th>
              <th className="text-left text-gray-400 pb-2">Per‑row margin</th>
              <th className="text-left text-gray-400 pb-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const o = originalById[r.id];

              // Saved margin (server)
              const savedMargin = o ? marginPctFrom(o.units_per_credit, o.platform_cost_per_unit) : 0;

              // Edited (current on screen)
              const editedMargin = marginPctFrom(r.units_per_credit, r.platform_cost_per_unit);

              // Current effective row slider value (when not using master)
              const rowSliderValue = sameMarginForAll
                ? masterMarginPct
                : rowMargins[r.id] ?? clamp(Math.round(editedMargin), SLIDER_MIN, SLIDER_MAX);

              return (
                <tr key={r.id} className="border-b border-gray-700 last:border-b-0 align-top">
                  <td className="py-3">
                    <div className="text-white capitalize">{r.service_type.replace("_", " ")}</div>
                    <div className="text-white text-xs">{r.model_used === null || r.model_used === "" || r.model_used === undefined ? "" : `(${r.model_used})`}</div>
                  </td>
                  <td className="py-3">
                    <div className="text-white">{r.unit_type}</div>
                  </td>

                  {/* platform_cost_per_unit (editable) + current */}
                  <td className="py-3 text-right">
                    <input
                      type="number"
                      min={0}
                      step="0.000001"
                      value={o?.platform_cost_per_unit?.toFixed(8)}
                      onChange={(e) =>
                        updateRowField(r.id, "platform_cost_per_unit", Number(e.target.value))
                      }
                      className="w-40 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-white text-right"
                    />
                    <div className="text-xs text-gray-400 mt-1">
                      Current: <span className="text-gray-300">${o?.platform_cost_per_unit?.toFixed(8)}</span>
                    </div>
                  </td>

                  {/* units_per_credit (editable) + current */}
                  <td className="py-3 text-right">
                    <Tooltip
                      content={getCalculationSummary(
                        r.platform_cost_per_unit,
                        r.units_per_credit,
                        editedMargin,
                        r.unit_type
                      )}
                    >
                      <input
                        type="number"
                        min={0.000001}
                        step="0.000001"
                        value={r.units_per_credit}
                        onChange={(e) => updateRowField(r.id, "units_per_credit", Number(e.target.value))}
                        className="w-40 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-white text-right cursor-help"
                      />
                    </Tooltip>
                    <div className="text-xs text-gray-400 mt-1">
                      Current: <span className="text-gray-300">{o?.units_per_credit?.toFixed(6)}</span>
                    </div>
                  </td>

                  {/* read-only credit value */}
                  <td className="py-3 text-right text-gray-300">${r.credit_value_usd?.toFixed(2)}</td>

                  {/* margin% saved vs edited — ALWAYS correct & live */}
                  <td className="py-3 text-right">
                    <div className="flex flex-col items-end">
                      <span className={`font-semibold ${savedMargin >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {pct(savedMargin).toFixed(1)}% <span className="text-gray-400">(current)</span>
                      </span>
                      <span className={`font-semibold ${editedMargin >= 0 ? "text-green-400" : "text-red-400"}`}>
                        {pct(editedMargin).toFixed(1)}% <span className="text-gray-400">(edited)</span>
                      </span>
                    </div>
                  </td>

                  {/* per-row margin controls */}
                  <td className="py-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min={SLIDER_MIN}
                        max={SLIDER_MAX}
                        step={1}
                        disabled={sameMarginForAll}
                        value={rowSliderValue}
                        onChange={(e) => applyRowMargin(r.id, Number(e.target.value))}
                        className="w-48 accent-blue-500"
                      />
                      <input
                        type="number"
                        min={SLIDER_MIN}
                        max={SLIDER_MAX}
                        step={1}
                        disabled={sameMarginForAll}
                        value={rowSliderValue}
                        onChange={(e) => applyRowMargin(r.id, Number(e.target.value))}
                        className="w-16 px-2 py-1 bg-gray-900 border border-gray-700 rounded text-white text-right"
                      />
                      <span className="text-white">%</span>
                    </div>
                  </td>

                  {/* actions */}
                  <td className="py-3">
                    <button
                      onClick={() => resetRowToCurrent(r.id)}
                      className="px-3 py-1 rounded bg-gray-700 text-white border border-gray-600 hover:bg-gray-600"
                    >
                      Reset row
                    </button>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-gray-400 py-12">
                  No active configurations found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-gray-500">
        Only <code>platform_cost_per_unit</code> and <code>units_per_credit</code> are editable. Others are read‑only.
      </div>

      {error && (
        <div className="bg-red-900 border border-red-700 rounded-lg p-4 text-red-200">
          {error}
        </div>
      )}
    </div>
  );
};

export default CreditConfigPage;
